<template>
  <ZeroState v-if="!items.length">This workflow has no {{ condition.lowercasePlural }}</ZeroState>

  <div class="flex flex-col gap-4 mb-10">
    <div v-for="(item, index) in items" :key="item.id">
      <p
        v-if="index !== 0"
        class="text-xs text-gray-400 font-bold border-2 border-gray-200 rounded-full px-2 py-1 inline-block mb-2 bg-gray-100"
      >
        AND
      </p>
      <div v-if="item.type === 'Condition'">
        <ConditionItem :id="`${conditionEditor.type}_item_${index}`" :expression-condition="item" :index="index" />
      </div>
      <div v-if="item.type === 'Group'">Subgroups are not supported</div>
    </div>
  </div>

  <div class="flex flex-col gap-6 justify-between lg:flex-row">
    <div
      class="text-blue-700 flex items-center gap-1 cursor-pointer hover:underline mt-2 self-start"
      @click="conditionEditor.addCondition"
    >
      <vue-feather type="plus-circle" class="w-4" />
      <span> Add {{ condition.capital }} </span>
    </div>
    <div>
      <div v-if="conditionEditor.isConditionNew.value" class="flex gap-2">
        <Button class="self-start" variant="default" :disabled="!items.length" @click="createCondition">
          Create {{ condition.capitalPlural }}
        </Button>
      </div>
      <div v-else class="flex gap-2">
        <Button
          class="self-start"
          variant="outline"
          :disabled="!conditionEditor.hasStagedChanges.value"
          @click="conditionEditor.discardChanges"
          >Discard Changes</Button
        >

        <Button
          class="self-start"
          variant="default"
          :disabled="!conditionEditor.hasStagedChanges.value"
          @click="displaySaveChangesModal"
          >Save Changes</Button
        >
      </div>
    </div>
  </div>

  <Modal
    v-if="showSaveChangesModal"
    title="Save Changes"
    :body="`Updating the ${condition.lowercasePlural} will replace the current state with the new changes. Any modifications you've made will be applied, and the previous state will no longer be retained.`"
    action="Save Changes"
    action-color="indigo"
    @close-modal="showSaveChangesModal = false"
    @handle-action="handleUpdateCondition"
  />
</template>

<script setup lang="ts">
import ConditionItem from './ConditionItem.vue';
import { Button } from '@/components/ui/button';
import { useWorkflowCondition } from './useWorkflowCondition';
import Modal from '@/components/shared/Modal.vue';
import ZeroState from '@/components/ZeroState.vue';
import { useInjectedConditionEditor } from './useWorkflowConditionEditorProvider';
import { computed } from 'vue';
import { useConditionDisplay } from './useConditionDisplay';

const { conditionDisplay } = useConditionDisplay();

const conditionEditor = useInjectedConditionEditor();
const items = computed(() => conditionEditor.localExpression.value.items);
const condition = conditionDisplay(conditionEditor.type);

const { showSaveChangesModal, createCondition, updateCondition, deleteCondition, displaySaveChangesModal } =
  useWorkflowCondition();

const handleUpdateCondition = async () => {
  if (conditionEditor.localExpression.value.items.length === 0) {
    await deleteCondition();
  } else {
    await updateCondition();
  }
};
</script>
